import { useState } from 'react'
import { MoreHorizontal } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useBoardEdit } from '@/features/ogsm/board-edit-context'
import { DeleteNodeDialog } from '@/components/custom/ogsm/delete-node-dialog'
import { LinkMetricDialog } from '@/components/custom/ogsm/link-metric-dialog'
import { ReviseTargetDialog } from '@/components/custom/ogsm/revise-target-dialog'
import { OGSM_COPY } from '@/features/ogsm/ogsm-labels'
import { useOgsmApi } from '@/hooks/use-ogsm'
import type { OgsmBoardNode } from '@/types/api'

/** Actions on one node. Absent unless the backend lets the structure change. */
export function NodeMenu({
  node,
  kind,
}: {
  node: OgsmBoardNode
  kind: string
}) {
  const { companyId, session, saveNode, editNode } = useBoardEdit()
  // The portal has no metric source (G2): linking is hidden, flagging is not.
  const { metricLinking } = useOgsmApi(companyId)
  const canLink = node.canLinkMetric && metricLinking
  const [deleting, setDeleting] = useState(false)
  const [linking, setLinking] = useState(false)
  const [revising, setRevising] = useState(false)
  // An open revise dialog stays mounted when a refused save turns the session read-only.
  if (!session.structureEditable && !revising) return null

  return (
    <>
      {session.structureEditable && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon-xs"
              aria-label={OGSM_COPY.actions(kind)}
            >
              <MoreHorizontal />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem onSelect={() => editNode(node)}>
              {OGSM_COPY.edit}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setRevising(true)}>
              {node.depth === 0
                ? OGSM_COPY.reviseGoal
                : OGSM_COPY.reviseMeasure}
            </DropdownMenuItem>
            {node.canFlagForScorecard && (
              <DropdownMenuCheckboxItem
                checked={node.includeInScorecard}
                onCheckedChange={(checked) =>
                  saveNode(node.id, { includeInScorecard: checked })
                }
              >
                {OGSM_COPY.includeInScorecard}
              </DropdownMenuCheckboxItem>
            )}
            {canLink && (
              <DropdownMenuItem onSelect={() => setLinking(true)}>
                {OGSM_COPY.linkMetric}
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive"
              onSelect={() => setDeleting(true)}
            >
              {OGSM_COPY.delete}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      <DeleteNodeDialog
        node={node}
        kind={kind}
        open={deleting}
        onOpenChange={setDeleting}
      />
      <ReviseTargetDialog
        node={node}
        open={revising}
        onOpenChange={setRevising}
      />
      {canLink && (
        <LinkMetricDialog
          node={node}
          open={linking}
          onOpenChange={setLinking}
        />
      )}
    </>
  )
}
